import React, { memo, useEffect, useMemo, useRef, useState } from 'react';

import { zonedWallTimeToUtcIso, utcIsoToZonedWallTime } from '../../../utils/schedule-helper';

const pad = (n) => String(n).padStart(2, '0');

// A wall-clock time can be nonexistent in Eastern on the spring-forward day,
// where the clock jumps 2:00 AM → 3:00 AM (so 2:00–2:59 AM don't exist).
// Offering such a slot lets the editor pick a time Braze can't deliver at —
// zonedWallTimeToUtcIso shifts it to a different hour. An Eastern round-trip
// that lands on a different wall-clock means the time is nonexistent, so we
// drop it. (The fall-back repeated hour round-trips to its first occurrence and
// is kept.)
const existsInEastern = (year, month, day, h, m) => {
  const iso = zonedWallTimeToUtcIso(new Date(year, month, day, h, m));
  const back = iso ? utcIsoToZonedWallTime(iso) : null;

  return Boolean(back) && back.getHours() === h && back.getMinutes() === m;
};

// 24h → "2:30 PM"
const fmt12 = (h, m) => {
  const ampm = h < 12 ? 'AM' : 'PM';
  const hr = h % 12 === 0 ? 12 : h % 12;

  return `${hr}:${pad(m)} ${ampm}`;
};

const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

// Lenient parse: "2:30 PM", "2:30pm", "230 pm", "14:30", "2 pm" → { h, m } | null.
const parseTime = (str) => {
  const m = String(str).trim().toLowerCase().match(/^(\d{1,2})[:.]?(\d{2})?\s*(am|pm)?$/);

  if (!m) return null;

  let h = Number(m[1]);
  const min = m[2] ? Number(m[2]) : 0;
  const ap = m[3];

  if (min > 59) return null;

  if (ap) {
    if (h < 1 || h > 12) return null;
    if (ap === 'pm' && h !== 12) h += 12;
    if (ap === 'am' && h === 12) h = 0;
  } else if (h > 23) {
    return null;
  }

  return { h, m: min };
};

/**
 * Modern, typeable time picker for the Send Later dialog (WWW-braze-update).
 * Type a time ("2:30 PM", "14:30") or pick from the dropdown. When the chosen
 * date is today, past times are hidden so you can't schedule into the past.
 *
 * Props
 *   value:       Date | null — the current schedule time (local fields = Eastern)
 *   minDate:     Date — Eastern "now" (its date decides "today"; its time is the floor)
 *   onChange:    (Date) => void — merges the picked time onto the chosen date
 *   placeholder: string
 */
const TimeSelect = ({ value, minDate, onChange, placeholder }) => {
  const base = value || minDate;
  const isToday = sameDay(base, minDate);
  const nowMins = minDate.getHours() * 60 + minDate.getMinutes();
  const baseYear = base.getFullYear();
  const baseMonth = base.getMonth();
  const baseDate = base.getDate();

  // 15-min slots; drop past slots when scheduling for today, and drop wall-clock
  // times that don't exist in Eastern on the spring-forward day.
  const slots = useMemo(() => {
    const out = [];

    for (let mins = 0; mins < 24 * 60; mins += 15) {
      if (isToday && mins <= nowMins) continue;

      const h = Math.floor(mins / 60);
      const m = mins % 60;

      if (!existsInEastern(baseYear, baseMonth, baseDate, h, m)) continue;

      out.push({ mins, h, m, label: fmt12(h, m) });
    }

    return out;
  }, [isToday, nowMins, baseYear, baseMonth, baseDate]);

  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  // Filter the dropdown only while the user is actively typing — focusing a field
  // that already holds a value should still show the full list, not just the match.
  const [dirty, setDirty] = useState(false);
  const wrapRef = useRef(null);

  // Keep the field text in sync with the committed value.
  useEffect(() => {
    setText(value ? fmt12(value.getHours(), value.getMinutes()) : '');
  }, [value]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;

    const onDocMouseDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };

    document.addEventListener('mousedown', onDocMouseDown);

    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const commit = (h, m) => {
    const next = new Date(base);

    next.setHours(h, m, 0, 0);
    onChange(next);
  };

  const handleType = (e) => {
    const v = e.target.value;

    setText(v);
    setDirty(true);

    const parsed = parseTime(v);

    // Commit only a valid, non-past (for today), existent Eastern time; the
    // field text keeps what was typed until blur re-syncs it.
    if (
      parsed
      && !(isToday && parsed.h * 60 + parsed.m <= nowMins)
      && existsInEastern(baseYear, baseMonth, baseDate, parsed.h, parsed.m)
    ) {
      commit(parsed.h, parsed.m);
    }
  };

  const handleBlur = () => {
    // Re-sync the display to the committed value (discards half-typed/invalid text).
    setText(value ? fmt12(value.getHours(), value.getMinutes()) : '');
  };

  const q = text.trim().toLowerCase();
  const filtered = dirty && q ? slots.filter((s) => s.label.toLowerCase().includes(q)) : slots;
  const selectedMins = value ? value.getHours() * 60 + value.getMinutes() : -1;

  return (
    <div className='time-select' ref={wrapRef}>
      <input
        type='text'
        className='form-control time-select__input'
        value={text}
        placeholder={placeholder}
        aria-label='Time'
        onChange={handleType}
        onFocus={() => { setDirty(false); setOpen(true); }}
        onBlur={handleBlur}
      />
      {open && filtered.length > 0 && (
        <ul className='time-select__menu'>
          {filtered.map((s) => (
            <li
              key={s.mins}
              className={`time-select__option${s.mins === selectedMins ? ' time-select__option--selected' : ''}`}
              // mousedown (not click) so it fires before the input's blur
              onMouseDown={() => { commit(s.h, s.m); setText(s.label); setDirty(false); setOpen(false); }}
            >
              {s.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default memo(TimeSelect);
